import React, { useMemo } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, ReferenceArea, Tooltip, CartesianGrid } from "recharts";
import { X, CheckCircle2, ArrowDownCircle, ArrowUpCircle, ChartSpline } from "lucide-react";
import { canonReadings } from "./waterChem";

const fmt = (d) => new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" });
const stateOf = (v, min, max) => (v < min ? "low" : v > max ? "high" : "ok");

function ResultChip({ p, min, max }) {
  const s = stateOf(p.value, min, max);
  const Icon = s === "ok" ? CheckCircle2 : s === "low" ? ArrowDownCircle : ArrowUpCircle;
  return (
    <div className="flex items-center gap-1.5" data-testid="wt-hist-chip">
      <Icon className={`h-6 w-6 ${s === "ok" ? "text-emerald-600" : "text-rose-600"}`} />
      <div className="leading-tight"><div className="font-black text-[14px] text-[#1a2b3c]">{p.value}</div><div className="text-[10px] text-slate-500">{p.label}</div></div>
    </div>
  );
}

export function ReadingHistory({ param, tests, onClose }) {
  const { key, label, min, max, unit } = param;
  const pts = useMemo(() => tests
    .filter((t) => Number.isFinite(Number(canonReadings(t.readings)[key])))
    .map((t) => ({ date: t.test_date || t.created_at, value: Number(canonReadings(t.readings)[key]) }))
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(-10)
    .map((p) => ({ ...p, label: fmt(p.date) })), [tests, key]);
  const top = Math.max(max * 1.3, ...pts.map((p) => p.value * 1.1), 1);
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 flex items-center justify-center p-3" onClick={onClose} data-testid="wt-history-popup">
      <div className="w-full max-w-xl bg-white border border-[#7ba7d9] rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-4 py-2.5 border-b border-[#7ba7d9] bg-[#dbe9f7] rounded-t-xl">
          <span className="font-black text-[#123a63] flex items-center gap-2"><ChartSpline className="h-4 w-4" /> {label} history</span>
          <button onClick={onClose} data-testid="wt-history-close" className="rounded-full p-1 hover:bg-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4">
          {pts.length === 0 ? (
            <p className="text-center text-[13px] text-slate-500 py-10" data-testid="wt-history-empty">No previous {label} results for this body of water yet.</p>
          ) : (
            <>
              <div className="h-56" data-testid="wt-history-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={pts} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid stroke="#eef2f7" vertical={false} />
                    <ReferenceArea y1={min} y2={max} fill="#22c55e" fillOpacity={0.12} stroke="#16a34a" strokeDasharray="4 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, Math.ceil(top)]} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => [`${v}${unit ? " " + unit : ""}`, label]} />
                    <Line type="monotone" dataKey="value" stroke="#0ea5e9" strokeWidth={2.5}
                      dot={({ cx, cy, payload }) => <circle key={payload.date} cx={cx} cy={cy} r={4.5} fill={stateOf(payload.value, min, max) === "ok" ? "#16a34a" : "#dc2626"} />} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Green band = ideal range ({min} – {max}{unit ? " " + unit : ""})</div>
              <div className="font-black text-[12px] text-[#123a63] mt-3 mb-1.5">Latest results</div>
              <div className="flex flex-wrap justify-between gap-3">
                {pts.slice(-5).map((p) => <ResultChip key={p.date} p={p} min={min} max={max} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
